import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import EventLayout from "@/components/event/EventLayout";
import EventAgenda from "@/components/event/EventAgenda";
import ProgramBookletCompact from "@/components/event/ProgramBookletCompact";
import AddToHomeScreen from "@/components/event/AddToHomeScreen";
import { Button } from "@/components/ui/button";
import { eventApi, humanDay, markCheckedIn, getCheckedIn, clearCheckedIn, EVENT_DAY_FRIDAY, EVENT_DAY_SATURDAY } from "@/lib/eventHub";
import { Loader2, Search, UserPlus, ChevronLeft, CheckCircle2, QrCode } from "lucide-react";

const CATEGORIES = [
  "Guest", "Honoree", "Sponsor", "Speaker", "Panelist", "Trailblazer",
  "Volunteer", "Board Member", "Vendor", "Staff", "Media", "Other",
];

export default function CheckIn() {
  const navigate = useNavigate();
  // Day-lock via ?day=YYYY-MM-DD (from EventDayHub). Locks the day dropdowns so a
  // Friday attendee cannot accidentally check in Saturday (spec §2).
  const dayParam = typeof window !== "undefined" ? new URLSearchParams(window.location.search).get("day") : null;
  const lockedDay = dayParam === "2026-07-17" || dayParam === "2026-07-18" ? dayParam : null;
  const [step, setStep] = useState("search"); // search | pick_match | host_or_guest | confirm | walkin
  const [search, setSearch] = useState({ query: "", email: "", phone: "", first_name: "", last_name: "" });
  const [loading, setLoading] = useState(false);
  const [matches, setMatches] = useState([]);
  const [selected, setSelected] = useState(null);
  const [eventDay, setEventDay] = useState(lockedDay || EVENT_DAY_FRIDAY);
  const [walkinForm, setWalkinForm] = useState({
    first_name: "", last_name: "", email: "", phone: "", organization: "",
    city: "", state: "", categories: ["Guest"], event_day: lockedDay || EVENT_DAY_FRIDAY, consent_email: false, consent_sms: true,
  });
  const [existingCheckin, setExistingCheckin] = useState(null);

  useEffect(() => {
    const stored = getCheckedIn();
    if (stored) setExistingCheckin(stored);
    eventApi.track("checkin_start");
  }, []);

  const handleSearch = async (e) => {
    e?.preventDefault?.();
    const raw = (search.query || "").trim();
    if (!raw && !search.email && !search.phone && !search.first_name && !search.last_name) {
      toast.error("Enter your name, email, or phone to search");
      return;
    }
    setLoading(true);
    try {
      const body = { ...search, query: raw };
      const { data } = await eventApi.searchAttendees(body);
      setMatches(data.matches || []);
      if ((data.matches || []).length === 0) {
        // No match → walk-in flow with prefilled data
        setWalkinForm((w) => ({
          ...w,
          email: raw.includes("@") ? raw : (search.email || ""),
          phone: /\d{3,}/.test(raw) && !raw.includes("@") ? raw : (search.phone || ""),
          first_name: search.first_name || (!raw.includes("@") && !/\d/.test(raw) ? raw.split(" ")[0] || "" : ""),
          last_name: search.last_name || (!raw.includes("@") && !/\d/.test(raw) ? raw.split(" ").slice(1).join(" ") || "" : ""),
        }));
        setStep("walkin");
        toast.info("No registration found — please check in as a walk-in.");
      } else if (data.matches.length === 1) {
        const m = data.matches[0];
        // Real-time short-circuit · if a volunteer already checked this guest
        // in (for the day they're checking in for, or any day if not locked),
        // skip Confirm and jump straight to the success screen. This gives the
        // guest instant "you're already in" feedback without another tap.
        const targetDay = lockedDay || eventDay;
        const existingCi = (m.check_ins || []).find(
          (ci) => (ci.status === "checked_in" || ci.status === "success" || !ci.status)
            && ci.event_day === targetDay
        );
        if (existingCi) {
          markCheckedIn({
            attendee_id: m.id,
            attendee_name: `${m.first_name} ${m.last_name}`.trim(),
            event_day: existingCi.event_day,
          });
          navigate("/event/checkin/success", {
            state: {
              attendee: m,
              event_day: existingCi.event_day,
              checked_in_at: existingCi.checked_in_at,
              already_checked_in: true,
            },
          });
          return;
        }
        setSelected(m);
        // Host-ticket guest-check-in correctness: if this attendee is also a
        // host with unclaimed tickets, ask whether they are the host OR their
        // guest before checking in. Prevents guests from checking in as the
        // host and losing their identity.
        if (m.is_host && (m.remaining_tickets ?? 0) > 0) {
          setStep("host_or_guest");
        } else {
          setStep("confirm");
        }
      } else {
        setStep("pick_match");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Search failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const doConfirm = async () => {
    if (!selected) return;
    setLoading(true);
    try {
      const { data } = await eventApi.confirmCheckIn({ attendee_id: selected.id, event_day: eventDay });
      markCheckedIn({
        attendee_id: data.attendee.id,
        attendee_name: `${data.attendee.first_name} ${data.attendee.last_name}`.trim(),
        event_day: eventDay,
      });
      const checkedInAt = (data.check_in?.multi?.[0]?.checked_in_at) || data.check_in?.checked_in_at;
      const alreadyIn = data.check_in?.already_checked_in || data.check_in?.multi?.some?.(m => m.already_checked_in);
      navigate("/event/checkin/success", {
        state: {
          attendee: data.attendee,
          event_day: eventDay,
          checked_in_at: checkedInAt,
          already_checked_in: alreadyIn,
        },
      });
    } catch (err) {
      const d = err?.response?.data?.detail;
      // 423 with structured detail = check-in window not open (spec §2)
      if (d && typeof d === "object" && d.error === "check_in_not_open") {
        toast.error(d.message, { duration: 6000, description: `Opens ${d.opens_at_et || "on the event day"}` });
      } else if (d && typeof d === "object" && d.error === "check_in_closed") {
        toast.error(d.message, { duration: 6000 });
      } else {
        toast.error((typeof d === "string" ? d : d?.message) || "Check-in failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const doWalkIn = async (e) => {
    e?.preventDefault?.();
    if (!walkinForm.first_name.trim() || !walkinForm.last_name.trim()) {
      toast.error("First and last name are required.");
      return;
    }
    if (!walkinForm.event_day) {
      toast.error("Please select which event(s) you're attending.");
      return;
    }
    setLoading(true);
    try {
      // Send only name-required payload; email/phone flow through as blanks
      // when the guest declines to share them (still gets checked in, just
      // no post-event SMS/email).
      const payload = {
        first_name: walkinForm.first_name.trim(),
        last_name: walkinForm.last_name.trim(),
        email: walkinForm.email.trim() || undefined,
        phone: walkinForm.phone.trim() || "",
        event_day: walkinForm.event_day,
        organization: walkinForm.organization,
        city: walkinForm.city,
        state: walkinForm.state,
        categories: walkinForm.categories,
        consent_sms: walkinForm.consent_sms && !!walkinForm.phone.trim(),
        consent_email: walkinForm.consent_email && !!walkinForm.email.trim(),
      };
      const { data } = await eventApi.walkIn(payload);
      markCheckedIn({
        attendee_id: data.attendee.id,
        attendee_name: `${data.attendee.first_name} ${data.attendee.last_name}`.trim(),
        event_day: walkinForm.event_day,
      });
      const checkedInAt = (data.check_in?.multi?.[0]?.checked_in_at) || data.check_in?.checked_in_at;
      navigate("/event/checkin/success", {
        state: {
          attendee: data.attendee,
          event_day: walkinForm.event_day,
          checked_in_at: checkedInAt,
          walk_in: true,
        },
      });
    } catch (err) {
      const d = err?.response?.data?.detail;
      if (d && typeof d === "object" && d.error === "check_in_not_open") {
        toast.error(d.message, { duration: 6000, description: `Opens ${d.opens_at_et || "on the event day"}` });
      } else if (d && typeof d === "object" && d.error === "check_in_closed") {
        toast.error(d.message, { duration: 6000 });
      } else {
        toast.error((typeof d === "string" ? d : d?.message) || "Walk-in registration failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setSelected(null);
    setMatches([]);
    setStep("search");
    setSearch({ query: "", email: "", phone: "", first_name: "", last_name: "" });
  };

  return (
    <EventLayout showProgramBanner={false}>
      <div className="mx-auto max-w-lg px-5 py-6">
        {step !== "search" && (
          <button
            data-testid="checkin-back"
            onClick={reset}
            className="mb-4 inline-flex items-center gap-1 text-sm font-[Outfit] font-semibold text-emerald-950/70 hover:text-fuchsia-700"
          >
            <ChevronLeft size={18} /> Start over
          </button>
        )}

        <h1 className="font-[Cormorant_Garamond] text-3xl sm:text-4xl text-emerald-950 leading-tight mb-2">
          {step === "search" ? "Check In" : step === "confirm" ? "Confirm It's You" : step === "walkin" ? "Walk-In Check-In" : step === "host_or_guest" ? "Are you the host?" : "Choose Your Registration"}
        </h1>
        <p className="font-[Outfit] text-sm text-emerald-950/60 mb-4">
          {step === "search" ? "Start with your email, name, or mobile number." : step === "confirm" ? "Please confirm your registration before we check you in." : step === "walkin" ? "New to us? Tell us a bit about you." : step === "host_or_guest" ? "This host has extra seats. Let us know who's checking in." : "Which one is you?"}
        </p>

        {lockedDay && (
          <div
            data-testid="checkin-day-lock"
            className="mb-5 rounded-2xl bg-emerald-950 text-white px-4 py-3 flex items-center gap-3"
          >
            <span className="h-9 w-9 rounded-full bg-fuchsia-500 flex items-center justify-center shrink-0"><CheckCircle2 size={16} /></span>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] tracking-[0.22em] uppercase font-[Outfit] font-bold text-fuchsia-300">Checking in for</div>
              <div className="font-[Cormorant_Garamond] text-lg leading-tight">
                {lockedDay === "2026-07-17" ? "Friday · July 17 · 4:30 PM–8:30 PM" : "Saturday · July 18 · 1:00 PM–5:30 PM"}
              </div>
            </div>
            <Link to="/event" className="text-[10px] font-[Outfit] font-bold uppercase tracking-widest text-fuchsia-200 hover:text-white shrink-0">Change</Link>
          </div>
        )}

        {existingCheckin && step === "search" && (
          <div
            data-testid="checkin-already-badge"
            className="mb-5 rounded-2xl border-2 border-emerald-100 bg-emerald-50 p-4"
          >
            <div className="flex items-center gap-2 mb-1">
              <CheckCircle2 size={18} className="text-emerald-700" />
              <span className="text-[11px] tracking-[0.16em] uppercase font-[Outfit] font-bold text-emerald-800">
                Recently Checked In
              </span>
            </div>
            <div className="font-[Cormorant_Garamond] text-lg text-emerald-950">
              {existingCheckin.attendee_name}
            </div>
            <div className="text-xs text-emerald-950/60 font-[Outfit] mt-1">
              {existingCheckin.event_day === "both" ? "Both Days" : humanDay(existingCheckin.event_day)}
            </div>
            <div className="flex gap-2 mt-3">
              <Link
                to="/event/program"
                data-testid="checkin-open-program"
                className="text-xs font-[Outfit] font-semibold text-fuchsia-700 hover:underline"
              >
                Open today’s program →
              </Link>
              <span className="text-emerald-900/20">·</span>
              <button
                onClick={() => { clearCheckedIn(); setExistingCheckin(null); }}
                data-testid="checkin-not-you"
                className="text-xs font-[Outfit] font-semibold text-emerald-950/60 hover:text-fuchsia-700"
              >
                Not you? Check in another guest
              </button>
            </div>
          </div>
        )}

        {/* SEARCH STEP */}
        {step === "search" && (
          <>
            {/* PWA install CTA — one-tap "Add to Home Screen" for attendees */}
            <div className="mb-4 flex justify-center">
              <AddToHomeScreen variant="pill" />
            </div>

            {/* Program booklet (spec §5 · restore booklet visibility on Check-In) */}
            <ProgramBookletCompact
              lockedDay={lockedDay}
              className="mb-5"
              heading={lockedDay === "2026-07-17" ? "Friday · VIP Program" : lockedDay === "2026-07-18" ? "Saturday · Business Showers Program" : "Program Booklet"}
            />

          <form onSubmit={handleSearch} data-testid="checkin-search-form" className="space-y-4">
            <label className="block">
              <span className="text-[11px] font-[Outfit] font-semibold uppercase tracking-wider text-emerald-950/70">Email, name, or mobile</span>
              <div className="mt-1 relative">
                <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-emerald-950/40" />
                <input
                  id="checkin-search-input"
                  data-testid="checkin-search-input"
                  type="text"
                  autoFocus
                  autoComplete="email"
                  value={search.query}
                  onChange={(e) => setSearch({ ...search, query: e.target.value })}
                  className="w-full h-14 rounded-xl border-2 border-emerald-950/10 bg-white pl-11 pr-4 font-[Outfit] text-base text-emerald-950 focus:border-fuchsia-500 outline-none"
                  placeholder="e.g. yourname@email.com"
                />
              </div>
            </label>
            <details className="text-sm">
              <summary
                data-testid="checkin-more-fields"
                className="cursor-pointer font-[Outfit] text-emerald-950/70 hover:text-fuchsia-700"
              >
                Or search with name + phone separately
              </summary>
              <div className="mt-3 grid grid-cols-2 gap-2">
                <input
                  data-testid="checkin-first-name"
                  type="text" placeholder="First name" autoComplete="given-name"
                  value={search.first_name}
                  onChange={(e) => setSearch({ ...search, first_name: e.target.value })}
                  className="h-12 rounded-xl border-2 border-emerald-950/10 bg-white px-3 font-[Outfit] text-sm focus:border-fuchsia-500 outline-none"
                />
                <input
                  data-testid="checkin-last-name"
                  type="text" placeholder="Last name" autoComplete="family-name"
                  value={search.last_name}
                  onChange={(e) => setSearch({ ...search, last_name: e.target.value })}
                  className="h-12 rounded-xl border-2 border-emerald-950/10 bg-white px-3 font-[Outfit] text-sm focus:border-fuchsia-500 outline-none"
                />
                <input
                  data-testid="checkin-phone"
                  type="tel" inputMode="tel" placeholder="Mobile number" autoComplete="tel"
                  value={search.phone}
                  onChange={(e) => setSearch({ ...search, phone: e.target.value })}
                  className="col-span-2 h-12 rounded-xl border-2 border-emerald-950/10 bg-white px-3 font-[Outfit] text-sm focus:border-fuchsia-500 outline-none"
                />
              </div>
            </details>

            <Button
              type="submit"
              disabled={loading}
              data-testid="checkin-search-submit"
              className="w-full h-14 rounded-xl bg-fuchsia-600 hover:bg-fuchsia-700 text-white font-[Outfit] font-bold text-base"
            >
              {loading ? <Loader2 className="animate-spin" size={18} /> : <><Search size={18} className="mr-2" />Find My Registration</>}
            </Button>

            <button
              type="button"
              data-testid="checkin-walkin-cta"
              onClick={() => setStep("walkin")}
              className="w-full py-3 rounded-xl border-2 border-emerald-950/15 bg-white text-emerald-950 font-[Outfit] font-semibold text-sm hover:border-fuchsia-400 hover:text-fuchsia-700 transition-colors"
            >
              <UserPlus size={16} className="inline-block mr-2 -mt-0.5" />
              I’m a walk-in guest
            </button>

            <Link
              data-testid="checkin-guest-of-host-link"
              to="/event/guest-checkin"
              className="block w-full text-center py-2 rounded-lg text-xs font-[Outfit] font-semibold text-emerald-950/50 hover:text-fuchsia-700 hover:underline"
            >
              Attending as a guest of a host? Tap here →
            </Link>
          </form>

          {/* Program agenda (spec §5 · restore on Check-In) */}
          <div className="mt-8" data-testid="checkin-agenda-section">
            <EventAgenda compact day={lockedDay} title={lockedDay ? "Tonight’s program" : "Event agenda"} />
          </div>

          {/* Check-in QR (spec §6 · restore visible QR) */}
          <details className="mt-6 rounded-2xl border-2 border-fuchsia-200 bg-white overflow-hidden" data-testid="checkin-qr-toggle">
            <summary className="cursor-pointer flex items-center gap-2 px-4 py-3 text-xs font-[Outfit] font-bold uppercase tracking-widest text-fuchsia-800 select-none">
              <QrCode size={14} /> Show check-in QR code
            </summary>
            <div className="p-5 flex flex-col items-center gap-3 bg-fuchsia-50/40" data-testid="checkin-qr-panel">
              <QRCodeSVG
                data-testid="checkin-qr-svg"
                value={typeof window !== "undefined" ? `${window.location.origin}/event/checkin` : ""}
                size={220}
                bgColor="#ffffff"
                fgColor="#052e16"
                includeMargin
              />
              <div className="text-[10px] font-[Outfit] text-emerald-950/60 text-center">
                Point a camera here — no typing, no app.<br />
                Volunteers: print, laminate, and post at each entrance.
              </div>
            </div>
          </details>
          </>
        )}

        {/* PICK MATCH STEP */}
        {step === "pick_match" && (
          <div data-testid="checkin-match-list" className="space-y-3">
            {matches.map((m) => (
              <button
                key={m.id}
                data-testid={`checkin-match-${m.id}`}
                onClick={() => {
                  setSelected(m);
                  // Same host-vs-guest chooser logic as single-match branch —
                  // prevents guests silently checking in as a multi-ticket host.
                  if (m.is_host && (m.remaining_tickets ?? 0) > 0) {
                    setStep("host_or_guest");
                  } else {
                    setStep("confirm");
                  }
                }}
                className="w-full text-left rounded-2xl border-2 border-emerald-950/10 bg-white p-4 hover:border-fuchsia-500 transition-colors"
              >
                <div className="font-[Cormorant_Garamond] text-xl text-emerald-950">
                  {m.first_name} {m.last_name}
                </div>
                <div className="text-xs text-emerald-950/60 font-[Outfit] mt-1">
                  {m.email || <span className="italic opacity-60">no email on file</span>}
                  {m.phone ? ` · ${m.phone}` : ""}
                </div>
                <div className="mt-2 flex flex-wrap gap-1">
                  {/* Day-registration badge · helps volunteers confirm the
                      guest is registered for the correct night (one day or
                      both). */}
                  <DayBadge days={m.registered_days || []} />
                  {m.is_host && (m.remaining_tickets ?? 0) > 0 && (
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-fuchsia-600 text-white font-[Outfit] font-bold uppercase tracking-wider">
                      Host · {m.remaining_tickets} seat{m.remaining_tickets === 1 ? "" : "s"} available
                    </span>
                  )}
                  {m.host_name && !m.is_host && (
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-fuchsia-50 text-fuchsia-800 font-[Outfit] font-semibold uppercase tracking-wider">
                      Guest of {m.host_name}
                    </span>
                  )}
                  {m.categories?.map((c) => (
                    <span key={c} className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-800 font-[Outfit] font-semibold uppercase tracking-wider">{c}</span>
                  ))}
                </div>
              </button>
            ))}
            <button
              onClick={() => setStep("walkin")}
              data-testid="checkin-none-match"
              className="w-full py-3 rounded-xl border-2 border-dashed border-emerald-950/20 font-[Outfit] font-semibold text-emerald-950/70 hover:border-fuchsia-500 hover:text-fuchsia-700"
            >
              None of these — I’m a walk-in
            </button>
          </div>
        )}

        {/* HOST-OR-GUEST STEP · host-ticket guest check-in correctness.
            Presented after the matched attendee is detected as a host with
            unclaimed tickets. Prevents guests from silently checking in
            under the host's name. */}
        {step === "host_or_guest" && selected && (
          <div data-testid="checkin-host-or-guest" className="space-y-4">
            <div className="rounded-2xl border-2 border-fuchsia-500 bg-white p-5">
              <div className="text-[10px] tracking-[0.2em] uppercase font-[Outfit] font-bold text-fuchsia-700">Registration Found</div>
              <div className="mt-2 font-[Cormorant_Garamond] text-2xl text-emerald-950 leading-tight">
                {selected.first_name} {selected.last_name}
              </div>
              <div className="mt-2 inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-fuchsia-600 text-white font-[Outfit] font-bold uppercase tracking-wider">
                Host · {selected.remaining_tickets} seat{selected.remaining_tickets === 1 ? "" : "s"} available
              </div>
            </div>

            <p className="text-sm font-[Outfit] text-emerald-950/70">
              Are you {selected.first_name} — or attending as their guest? Tap the one that matches you.
            </p>

            <button
              onClick={() => setStep("confirm")}
              data-testid="checkin-i-am-host"
              className="w-full rounded-2xl border-2 border-emerald-950 bg-emerald-950 hover:bg-emerald-900 text-white px-4 py-4 flex items-center gap-3 text-left transition-colors"
            >
              <span className="h-10 w-10 rounded-xl bg-fuchsia-500/30 text-fuchsia-100 flex items-center justify-center shrink-0">
                <CheckCircle2 size={18} />
              </span>
              <div className="flex-1 min-w-0">
                <div className="font-[Cormorant_Garamond] text-xl leading-tight">
                  Yes — I am {selected.first_name} {selected.last_name}
                </div>
                <div className="text-[11px] font-[Outfit] text-white/70">1-tap check-in · Your seats stay reserved for your guests</div>
              </div>
            </button>

            <button
              onClick={() => {
                const hostId = selected.host_record_id;
                const day = lockedDay || eventDay;
                if (!hostId) {
                  toast.error("Host record unavailable. Please ask a volunteer.");
                  return;
                }
                navigate(`/event/guest-checkin?host_id=${encodeURIComponent(hostId)}&day=${day}`);
              }}
              data-testid="checkin-i-am-guest"
              className="w-full rounded-2xl border-2 border-fuchsia-200 bg-fuchsia-50/40 hover:border-fuchsia-400 hover:bg-fuchsia-50 px-4 py-4 flex items-center gap-3 text-left transition-colors"
            >
              <span className="h-10 w-10 rounded-xl bg-fuchsia-100 text-fuchsia-700 flex items-center justify-center shrink-0">
                <UserPlus size={16} />
              </span>
              <div className="flex-1 min-w-0">
                <div className="font-[Cormorant_Garamond] text-xl leading-tight text-emerald-950">
                  I&rsquo;m attending as {selected.first_name}&rsquo;s guest
                </div>
                <div className="text-[11px] font-[Outfit] text-emerald-950/60">
                  Enter your details on the next screen · {selected.remaining_tickets} seat{selected.remaining_tickets === 1 ? "" : "s"} remaining
                </div>
              </div>
              <ChevronLeft size={16} className="rotate-180 text-fuchsia-700/60 shrink-0" />
            </button>

            <button
              onClick={reset}
              data-testid="checkin-host-or-guest-cancel"
              className="w-full py-2 text-sm font-[Outfit] font-semibold text-emerald-950/60 hover:text-fuchsia-700"
            >
              That&rsquo;s not me — search again
            </button>
          </div>
        )}

        {/* CONFIRM STEP */}
        {step === "confirm" && selected && (
          <div data-testid="checkin-confirm" className="space-y-4">
            <div className="rounded-2xl border-2 border-fuchsia-500 bg-white p-5">
              <div className="text-[10px] tracking-[0.2em] uppercase font-[Outfit] font-bold text-fuchsia-700">Registration Found</div>
              <div className="mt-2 font-[Cormorant_Garamond] text-2xl text-emerald-950 leading-tight">
                {selected.first_name} {selected.last_name}
              </div>
              <div className="text-sm text-emerald-950/60 font-[Outfit] mt-1">
                {selected.email || <span className="italic opacity-60">no email on file</span>}
              </div>
              <div className="mt-2 flex flex-wrap gap-1">
                {/* Day-registration badge · lets volunteers instantly see
                    whether the guest is on the list for Friday only,
                    Saturday only, or both nights. */}
                <DayBadge days={selected.registered_days || []} />
                {selected.host_name && !selected.is_host && (
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-fuchsia-50 text-fuchsia-800 font-[Outfit] font-semibold uppercase tracking-wider">
                    Guest of {selected.host_name}
                  </span>
                )}
                {selected.categories?.map((c) => (
                  <span key={c} className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-800 font-[Outfit] font-semibold uppercase tracking-wider">{c}</span>
                ))}
              </div>
            </div>

            <div>
              <div className="text-[11px] font-[Outfit] font-semibold uppercase tracking-wider text-emerald-950/70 mb-2">Which event today?</div>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { v: EVENT_DAY_FRIDAY, l: "Friday · Jul 17", sub: "4:30–8:30 PM" },
                  { v: EVENT_DAY_SATURDAY, l: "Saturday · Jul 18", sub: "1:00–5:30 PM" },
                ].map((opt) => (
                  <button
                    key={opt.v}
                    data-testid={`checkin-day-${opt.v}`}
                    onClick={() => setEventDay(opt.v)}
                    className={`rounded-xl py-3 font-[Outfit] font-bold text-sm transition-all ${
                      eventDay === opt.v
                        ? "bg-emerald-950 text-white shadow-md"
                        : "bg-white border-2 border-emerald-950/10 text-emerald-950"
                    }`}
                  >
                    <div>{opt.l}</div>
                    <div className={`text-[10px] font-normal mt-0.5 ${eventDay === opt.v ? "text-fuchsia-300" : "text-emerald-950/50"}`}>{opt.sub}</div>
                  </button>
                ))}
              </div>
              <p className="text-[10px] font-[Outfit] text-emerald-950/50 mt-2 italic">Attending both nights? Please check in separately for each day.</p>
            </div>

            <Button
              onClick={doConfirm}
              disabled={loading}
              data-testid="checkin-confirm-submit"
              className="w-full h-16 rounded-xl bg-fuchsia-600 hover:bg-fuchsia-700 text-white font-[Outfit] font-bold text-lg shadow-lg shadow-fuchsia-600/20"
            >
              {loading ? <Loader2 className="animate-spin" size={20} /> : "Yes, that's me — Check Me In"}
            </Button>
            <button
              onClick={reset}
              data-testid="checkin-confirm-not-me"
              className="w-full py-2 text-sm font-[Outfit] font-semibold text-emerald-950/60 hover:text-fuchsia-700"
            >
              That’s not me — search again
            </button>
          </div>
        )}

        {/* WALK-IN STEP */}
        {step === "walkin" && (
          <form onSubmit={doWalkIn} data-testid="checkin-walkin-form" className="space-y-3">
            <div className="rounded-xl border-2 border-emerald-100 bg-emerald-50/40 p-3 text-xs font-[Outfit] text-emerald-900">
              <strong className="font-bold">Name is all we need.</strong> Email and mobile are optional — leave them blank if the guest prefers not to share. They&rsquo;ll still be checked in, we just won&rsquo;t text or email them.
            </div>
            <div className="grid grid-cols-2 gap-2">
              <input
                data-testid="walkin-first-name"
                required type="text" placeholder="First name *" autoComplete="given-name"
                value={walkinForm.first_name}
                onChange={(e) => setWalkinForm({ ...walkinForm, first_name: e.target.value })}
                className="h-12 rounded-xl border-2 border-emerald-950/10 bg-white px-3 font-[Outfit] text-sm focus:border-fuchsia-500 outline-none"
              />
              <input
                data-testid="walkin-last-name"
                required type="text" placeholder="Last name *" autoComplete="family-name"
                value={walkinForm.last_name}
                onChange={(e) => setWalkinForm({ ...walkinForm, last_name: e.target.value })}
                className="h-12 rounded-xl border-2 border-emerald-950/10 bg-white px-3 font-[Outfit] text-sm focus:border-fuchsia-500 outline-none"
              />
            </div>
            <input
              data-testid="walkin-email"
              type="email" placeholder="Email (optional)" autoComplete="email"
              value={walkinForm.email}
              onChange={(e) => setWalkinForm({ ...walkinForm, email: e.target.value })}
              className="w-full h-12 rounded-xl border-2 border-emerald-950/10 bg-white px-3 font-[Outfit] text-sm focus:border-fuchsia-500 outline-none"
            />
            <input
              data-testid="walkin-phone"
              type="tel" inputMode="tel" placeholder="Mobile number (optional)" autoComplete="tel"
              value={walkinForm.phone}
              onChange={(e) => setWalkinForm({ ...walkinForm, phone: e.target.value })}
              className="w-full h-12 rounded-xl border-2 border-emerald-950/10 bg-white px-3 font-[Outfit] text-sm focus:border-fuchsia-500 outline-none"
            />
            <input
              data-testid="walkin-organization"
              type="text" placeholder="Business or organization (optional)"
              value={walkinForm.organization}
              onChange={(e) => setWalkinForm({ ...walkinForm, organization: e.target.value })}
              className="w-full h-12 rounded-xl border-2 border-emerald-950/10 bg-white px-3 font-[Outfit] text-sm focus:border-fuchsia-500 outline-none"
            />
            <div>
              <div className="text-[11px] font-[Outfit] font-semibold uppercase tracking-wider text-emerald-950/70 mb-2">Which event? *</div>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { v: EVENT_DAY_FRIDAY, l: "Friday · Jul 17", sub: "4:30–8:30 PM" },
                  { v: EVENT_DAY_SATURDAY, l: "Saturday · Jul 18", sub: "1:00–5:30 PM" },
                ].map((opt) => (
                  <button
                    type="button"
                    key={opt.v}
                    data-testid={`walkin-day-${opt.v}`}
                    onClick={() => setWalkinForm({ ...walkinForm, event_day: opt.v })}
                    className={`rounded-xl py-3 font-[Outfit] font-bold text-sm transition-all ${
                      walkinForm.event_day === opt.v
                        ? "bg-emerald-950 text-white shadow-md"
                        : "bg-white border-2 border-emerald-950/10 text-emerald-950"
                    }`}
                  >
                    <div>{opt.l}</div>
                    <div className={`text-[10px] font-normal mt-0.5 ${walkinForm.event_day === opt.v ? "text-fuchsia-300" : "text-emerald-950/50"}`}>{opt.sub}</div>
                  </button>
                ))}
              </div>
            </div>

            {(walkinForm.email.trim() || walkinForm.phone.trim()) && (
              <div className="space-y-1.5 pt-2 text-xs font-[Outfit] text-emerald-950/70">
                <div className="text-[10px] uppercase tracking-wider font-bold text-emerald-950/60">Optional messaging</div>
                {walkinForm.email.trim() && (
                  <label className="flex items-start gap-2">
                    <input
                      data-testid="walkin-consent-email"
                      type="checkbox"
                      checked={walkinForm.consent_email}
                      onChange={(e) => setWalkinForm({ ...walkinForm, consent_email: e.target.checked })}
                      className="mt-0.5 h-4 w-4 rounded border-emerald-950/20 text-fuchsia-600 focus:ring-fuchsia-500"
                    />
                    <span>Email me future Business Showers updates.</span>
                  </label>
                )}
                {walkinForm.phone.trim() && (
                  <label className="flex items-start gap-2">
                    <input
                      data-testid="walkin-consent-sms"
                      type="checkbox"
                      checked={walkinForm.consent_sms}
                      onChange={(e) => setWalkinForm({ ...walkinForm, consent_sms: e.target.checked })}
                      className="mt-0.5 h-4 w-4 rounded border-emerald-950/20 text-fuchsia-600 focus:ring-fuchsia-500"
                    />
                    <span>
                      Text me event-day reminders. Msg &amp; data rates may apply. 1–3 msgs per event. Reply STOP to opt out.{" "}
                      <a href="/privacy" target="_blank" rel="noreferrer" className="underline text-fuchsia-700 font-semibold">Privacy</a>
                      {" & "}
                      <a href="/terms" target="_blank" rel="noreferrer" className="underline text-fuchsia-700 font-semibold">Terms</a>.
                    </span>
                  </label>
                )}
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              data-testid="walkin-submit"
              className="w-full h-14 rounded-xl bg-fuchsia-600 hover:bg-fuchsia-700 text-white font-[Outfit] font-bold text-base"
            >
              {loading ? <Loader2 className="animate-spin" size={18} /> : "Check In as Walk-In"}
            </Button>
          </form>
        )}
      </div>
    </EventLayout>
  );
}

function ChooserRow({ label, hint, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className="w-full rounded-2xl border-2 border-emerald-100 bg-white hover:border-emerald-300 px-4 py-3 flex items-center gap-3 text-left transition-colors"
    >
      <span className="h-10 w-10 rounded-xl bg-emerald-100 text-emerald-800 flex items-center justify-center"><CheckCircle2 size={16} /></span>
      <div className="flex-1 min-w-0">
        <div className="font-[Cormorant_Garamond] text-lg leading-tight text-emerald-950">{label}</div>
        <div className="text-[11px] font-[Outfit] text-emerald-950/60">{hint}</div>
      </div>
    </button>
  );
}

// Day-registration badge for search-result rows. Renders a compact chip
// summarising whether the attendee is on the list for Friday only, Saturday
// only, or both nights — lets volunteers confirm the guest at a glance.
function DayBadge({ days }) {
  const hasFri = days.includes("2026-07-17");
  const hasSat = days.includes("2026-07-18");
  if (!hasFri && !hasSat) return null;
  const [label, cls] = hasFri && hasSat
    ? ["Both nights", "bg-emerald-950 text-white"]
    : hasFri
      ? ["Friday only", "bg-fuchsia-100 text-fuchsia-800"]
      : ["Saturday only", "bg-emerald-100 text-emerald-800"];
  return (
    <span
      data-testid={`day-badge-${hasFri ? "fri" : ""}${hasSat ? "sat" : ""}`}
      className={`text-[10px] px-2 py-0.5 rounded-full font-[Outfit] font-bold uppercase tracking-wider ${cls}`}
    >
      {label}
    </span>
  );
}
